import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Paper,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const ContractAssistant = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);

  // Fetch conversation list on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/assistant/conversations`);
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const handleNewChat = useCallback(async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/assistant/conversations`);
      const conv = res.data;
      setActiveConversationId(conv.id);
      setMessages([]);
      setConversations((prev) => [conv, ...prev]);
      setHistoryAnchorEl(null);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  }, []);

  const handleLoadConversation = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/assistant/conversations/${convId}`);
      setActiveConversationId(convId);
      setMessages(res.data.messages || []);
      setHistoryAnchorEl(null);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  }, []);

  const handleDeleteConversation = useCallback(async (e, convId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_BASE_URL}/api/assistant/conversations/${convId}`);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (activeConversationId === convId) {
        setActiveConversationId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [activeConversationId]);

  const handleSend = useCallback(async () => {
    const question = inputValue.trim();
    if (!question || loading) return;

    setInputValue('');

    let convId = activeConversationId;

    // Auto-create conversation if none active
    if (!convId) {
      try {
        const res = await axios.post(`${API_BASE_URL}/api/assistant/conversations`);
        convId = res.data.id;
        setActiveConversationId(convId);
        setConversations((prev) => [res.data, ...prev]);
      } catch (err) {
        console.error('Failed to create conversation:', err);
        return;
      }
    }

    // Optimistic user message
    const userMsg = { role: 'user', content: question, id: `temp-${Date.now()}` };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/assistant/conversations/${convId}/ask`,
        { question }
      );

      const assistantMsg = {
        role: 'assistant',
        content: res.data.answer,
        contract_references: res.data.contract_references,
        id: res.data.message_id,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // Refresh conversation list to update title/preview
      fetchConversations();
    } catch (err) {
      const errorMsg = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        id: `error-${Date.now()}`,
      };
      setMessages((prev) => [...prev, errorMsg]);
      console.error('Failed to send question:', err);
    } finally {
      setLoading(false);
    }
  }, [inputValue, loading, activeConversationId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const historyOpen = Boolean(historyAnchorEl);

  // Collapsed state — just show a vertical tab to expand
  if (collapsed) {
    return (
      <Paper
        onClick={onToggle}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 2,
          px: 0.5,
          height: '100%',
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(217, 138, 43, 0.08)',
            borderColor: 'rgba(217, 138, 43, 0.20)',
          },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <SmartToyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography
          sx={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            letterSpacing: '0.04em',
          }}
        >
          AI Assistant
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: 'rgba(217, 138, 43, 0.08)',
          borderBottom: '1px solid', borderBottomColor: 'divider',
          flexShrink: 0,
        }}
      >
        <SmartToyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', flex: 1 }}
        >
          AI Assistant
        </Typography>
        <Tooltip title="New chat">
          <IconButton
            size="small"
            onClick={handleNewChat}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Chat history">
          <IconButton
            size="small"
            onClick={(e) => setHistoryAnchorEl(e.currentTarget)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Collapse">
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* History Menu */}
        <Menu
          anchorEl={historyAnchorEl}
          open={historyOpen}
          onClose={() => setHistoryAnchorEl(null)}
          PaperProps={{
            sx: {
              maxHeight: 360,
              width: 300,
              bgcolor: 'background.paper',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleNewChat} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <AddIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="New Chat"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: 'primary.main' }}
            />
          </MenuItem>
          {conversations.length > 0 && <Divider />}
          {conversations.map((conv) => (
            <MenuItem
              key={conv.id}
              onClick={() => handleLoadConversation(conv.id)}
              selected={conv.id === activeConversationId}
              sx={{
                py: 1,
                '&.Mui-selected': { bgcolor: 'rgba(217, 138, 43, 0.08)' },
              }}
            >
              <ListItemIcon>
                <ChatBubbleOutlineIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.6 }} />
              </ListItemIcon>
              <ListItemText
                primary={conv.title || 'Untitled'}
                secondary={dayjs(conv.updated_at).fromNow()}
                primaryTypographyProps={{
                  fontSize: '0.82rem',
                  fontWeight: 500,
                  noWrap: true,
                  sx: { maxWidth: 180 },
                }}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
              <IconButton
                size="small"
                onClick={(e) => handleDeleteConversation(e, conv.id)}
                sx={{
                  ml: 0.5,
                  color: 'text.secondary',
                  opacity: 0.4,
                  '&:hover': { color: 'error.main', opacity: 1 },
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </MenuItem>
          ))}
          {conversations.length === 0 && (
            <MenuItem disabled>
              <ListItemText
                primary="No conversations yet"
                primaryTypographyProps={{ fontSize: '0.82rem', color: 'text.secondary' }}
              />
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {/* Welcome state */}
        {messages.length === 0 && !loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              opacity: 0.6,
              gap: 1,
            }}
          >
            <SmartToyIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.4 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Ask me anything about your contracts
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 240 }}>
              I can compare contracts, find specific terms, summarize key data, and more.
            </Typography>
          </Box>
        )}

        {/* Message bubbles */}
        {messages.map((msg, index) => (
          <MessageBubble
            key={msg.id || index}
            message={msg}
            onContractClick={(contractId) => navigate(`/contracts/${contractId}`)}
          />
        ))}

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
            <CircularProgress size={16} sx={{ color: 'primary.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Analyzing contracts...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          px: 2,
          py: 1.5,
          borderTop: '1px solid', borderTopColor: 'divider',
          flexShrink: 0,
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={3}
          size="small"
          placeholder="Ask about your contracts..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '0.85rem',
              bgcolor: 'background.default',
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!inputValue.trim() || loading}
          sx={{
            color: inputValue.trim() ? 'primary.main' : 'text.secondary',
            transition: 'color 0.2s ease',
            mb: 0.3,
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Message bubble sub-component
// ---------------------------------------------------------------------------

const MessageBubble = ({ message, onContractClick }) => {
  const isUser = message.role === 'user';
  const refs = message.contract_references || [];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          maxWidth: '88%',
          px: 1.8,
          py: 1.2,
          borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
          bgcolor: isUser ? 'rgba(217, 138, 43, 0.08)' : '#0E0F0C',
          border: isUser ? 'none' : '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            // Assistant bubble is always ink; user bubble follows the theme.
            color: isUser ? 'text.primary' : '#F4F2EC',
            fontSize: '0.84rem',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>

        {/* Contract reference chips */}
        {refs.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {refs.map((ref) => (
              <Chip
                key={ref.contract_id}
                icon={<DescriptionIcon sx={{ fontSize: '14px !important' }} />}
                label={ref.contract_name}
                size="small"
                onClick={() => onContractClick(ref.contract_id)}
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  bgcolor: 'rgba(217, 138, 43, 0.08)',
                  color: 'primary.main',
                  border: '1px solid rgba(217, 138, 43, 0.12)',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(217, 138, 43, 0.12)',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContractAssistant;
